/**
 * The original's keyboard menu, driven the way CIRCAD drives it.
 *
 * Every command is two keystrokes: a menu letter then a command letter.
 * `F` `S` saves, `N` `R` builds the ratsnest, `P` `W` starts a signal wire.
 * The codes are not invented — they are the two bytes of the original's
 * command constants, extracted from OG2.h, so the muscle memory transfers.
 *
 * Commands SirCAD does not implement are still listed, greyed. Hiding them
 * would misrepresent the program as complete; showing them says plainly what
 * the original does and what this does not do yet.
 */

import { MENUS, type Menu, type MenuCommand } from '@sircad/core/menu/commands.ts';
import { topicFor } from '@sircad/core/menu/help-sircad.ts';

/**
 * The one-line description shown when a menu entry is hovered.
 *
 * From the merged topics, so a command the original never had and a command
 * whose letters mean something else here both describe *this* program. There
 * used to be a table of tips beside this for exactly those two cases, which
 * meant the tooltip and the help topic were written in two places and could
 * disagree — and did.
 */
export function tipFor(code: string): string {
  // A moved command still keeps the original's help, which is filed under
  // the original's code.
  return topicFor(originalCode(code))?.tip ?? '';
}

/**
 * How a command came to be run.
 *
 * The same command means slightly different things depending on how it was
 * reached, and the two facts that matter are kept here rather than guessed at
 * by the handlers.
 */
export interface CommandInvocation {
  /**
   * Fired because its menu opened and it is that menu's default, rather than
   * because the user named it. Pressing `G` runs Grab Item to arm the tool; it
   * must not also pick something up.
   */
  fromDefault?: boolean;
  /**
   * Reached by typing its letters rather than by clicking the menu.
   *
   * A command driven from the keyboard is a decision already made — the hand is
   * on the keys and the next thing wanted is the drawing, not a dialog to
   * dismiss. One driven with the mouse is a browse, and behaves as it always
   * has.
   */
  viaKeyboard?: boolean;
}

export type CommandHandler = (how?: CommandInvocation) => void | Promise<void>;

/**
 * Commands SirCAD adds that the original never had.
 *
 * `commands.ts` is generated from the original's own header, so it cannot
 * carry these — anything added there would be lost the next time it is
 * extracted. They are declared here instead, and merged in wherever the menu
 * tree is consulted, so a new command is a normal command in every other
 * respect: it can be registered, shown, keyed and used in a macro.
 *
 * The codes have to be ones the original left free in that menu.
 */
export const EXTRA_COMMANDS: MenuCommand[] = [
  {
    code: 'FK', menu: 'F', key: 'K',
    symbol: 'File_KicadOut', label: 'KiCad Out',
  },
  // Gerber In. Not `FI`, which the original's lowercase `Fi` (New Sibling)
  // shadows — command letters are matched without regard to case.
  {
    code: 'FH', menu: 'F', key: 'H',
    symbol: 'File_GerberIn', label: 'Gerber In',
  },
  // Out to the original's own format, beside the other exports. Save As will
  // write one too if you name the file .PCB, but a translation you have to know
  // a filename trick to reach is one nobody finds.
  //
  // `J` says nothing about CIRCAD. It is what was left: of the File menu the
  // original leaves only J, W, Y and Z free, and W has gone to Rename. The menu
  // entry is how this is found; the letter is for afterwards.
  {
    code: 'FJ', menu: 'F', key: 'J',
    symbol: 'File_CircadOut', label: 'Export to CIRCAD…',
  },
  // Rename the open file. The original has no such command — a file there is
  // its path on disk — but here a sheet, a board and a text file all carry a
  // name of their own, and the only other way to change one was to Save As.
  //
  // `W` says nothing about renaming. It is what was left: letters are matched
  // without regard to case, and of the File menu the original leaves only J, W,
  // Y and Z free — `FR` would be reached by its own `Fr` (BOM In) and never
  // fire. The menu entry is how this is found; the letter is for afterwards.
  {
    code: 'FW', menu: 'F', key: 'W',
    symbol: 'File_Rename', label: 'Rename',
  },
  // Start the board for a design that has not got one. The original has no
  // such command because a CIRCAD file is one drawing and you open the board
  // as a file of its own; here a design is one board and any number of sheets,
  // so a schematic opened alone has a board that exists and was never opened.
  //
  // `Y` says nothing about boards. Of the File menu the original leaves only
  // J, W, Y and Z free, J and W are spent, and `FB` and `FI` are both shadowed
  // by commands of their own — letters are matched without regard to case, so
  // `FI` would be caught by `Fi` (New Sheet) and never fire. The menu entry is
  // how this is found; the letter is for afterwards.
  {
    code: 'FY', menu: 'F', key: 'Y',
    symbol: 'File_NewBoard', label: 'New Board',
  },
  // The reading end of Help ▸ Support. The original had no such thing — there
  // was nobody to report to but the post — so it takes a free letter.
  {
    code: '?B', menu: '?', key: 'B',
    symbol: 'Help_Reports', label: 'Reports',
  },
  // Install as an app. The original ran as one and had no need of the idea; a
  // page has to ask. `I` was free on the Help menu, and for once the obvious
  // letter was the one left over.
  {
    code: '?I', menu: '?', key: 'I',
    symbol: 'Help_Install', label: 'Install as App',
  },
  // The board as it would be made, flat and in the round. Neither is a zoom or
  // an edit, so they live in a menu of our own rather than borrowing letters
  // from one of the original's.
  {
    code: 'VB', menu: 'V', key: 'B',
    symbol: 'Views_BoardView', label: 'Board View',
  },
  {
    code: 'VR', menu: 'V', key: 'R',
    symbol: 'Views_Render', label: 'Render',
  },
  // Pin numbers over the pads. A way of looking at the board rather than a
  // change to it, so it belongs here beside the other two.
  {
    code: 'VP', menu: 'V', key: 'P',
    symbol: 'Views_PinNumbers', label: 'Pin Numbers',
  },
  // A photograph laid under the drawing to trace a board from. One command per
  // operation rather than a dialog of buttons: every one of these is two
  // keypresses, which is the whole point of the command codes, and lining a
  // photograph up means doing the same few things over and over.
  {
    code: 'VI', menu: 'V', key: 'I',
    symbol: 'Views_Image', label: 'Image…',
  },
  {
    code: 'VL', menu: 'V', key: 'L',
    symbol: 'Views_ImageLoad', label: 'Load Image',
  },
  {
    code: 'VM', menu: 'V', key: 'M',
    symbol: 'Views_ImageMove', label: 'Move Image',
  },
  {
    code: 'VS', menu: 'V', key: 'S',
    symbol: 'Views_ImageScale', label: 'Scale Image',
  },
  {
    code: 'VC', menu: 'V', key: 'C',
    symbol: 'Views_ImageCorner', label: 'Corner Image',
  },
  {
    code: 'VN', menu: 'V', key: 'N',
    symbol: 'Views_ImageNudge', label: 'Nudge Image',
  },
  {
    code: 'VT', menu: 'V', key: 'T',
    symbol: 'Views_ImageTurn', label: 'Turn Image',
  },
  {
    code: 'VF', menu: 'V', key: 'F',
    symbol: 'Views_ImageFade', label: 'Fade Image',
  },
  {
    code: 'VD', menu: 'V', key: 'D',
    symbol: 'Views_ImageDrop', label: 'Drop Image',
  },
  {
    code: 'SN', menu: 'S', key: 'N',
    symbol: 'Setup_SnapGrid', label: 'Snap Grid',
  },
  // The macro recorder. The original's keystroke macros are a written list of
  // command codes; these record what was actually done, clicks included, which
  // it had no way to express. F11 and F12 are the keys people will use — the
  // menu entries are how the feature is found in the first place.
  {
    code: 'SR', menu: 'S', key: 'R',
    symbol: 'Setup_MacroRecord', label: 'Record Macro (F11)',
  },
  {
    code: 'SB', menu: 'S', key: 'B',
    symbol: 'Setup_MacroPlay', label: 'Play Macro (F12)',
  },
  // How hard the board is going to be to route, before anything is moved.
  // Crossings and congestion, and the two placement faults that stop a router
  // dead. `P` for placement, and it was free.
  {
    code: 'NP', menu: 'N', key: 'P',
    symbol: 'Nets_Density', label: 'Placement Density',
  },
  // Put the parts where the netlist wants them. `P` had already gone to the
  // density report, which is the thing you read before running this; `B` was
  // the next letter free on the menu.
  {
    code: 'NB', menu: 'N', key: 'B',
    symbol: 'Nets_AutoPlace', label: 'Auto Place…',
  },
  // Route the one connection nearest the cursor. `J` for join, and it was
  // free — the whole-board router is Auto Route, which is not built yet.
  {
    code: 'NJ', menu: 'N', key: 'J',
    symbol: 'Nets_RouteOne', label: 'Route Connection',
  },
  // Pour the supplies. `E` for the plane it pours on, and it was free — the
  // original has Flood Fill under Block, which pours one layer you have chosen
  // for one signal you have named; this decides which nets want a plane at all
  // and reports what the pour threw away.
  {
    code: 'NE', menu: 'N', key: 'E',
    symbol: 'Nets_Planes', label: 'Pour Planes…',
  },
  // Every part on the sheet against the pattern it becomes. The original has
  // no such command — Net List In simply skips a package whose pattern is not
  // in the library, and the only account of it is a line in the summary after
  // the board has already been laid out short. `F` for footprint, and it was
  // free.
  {
    code: 'NF', menu: 'N', key: 'F',
    symbol: 'Nets_FootprintCheck', label: 'Footprint Check…',
  },
  // Cut the right angles out of the router's own copper and pull the vias it
  // paid for nothing. `W` for wiring, and it was free.
  {
    code: 'NW', menu: 'N', key: 'W',
    symbol: 'Nets_Tidy', label: 'Tidy Copper',
  },
  // Can this be made? Net Scan asks whether the circuit is right; this asks
  // whether a shop can build it. They belong in one menu because they are the
  // two ways a finished board fails. `D` was free.
  {
    code: 'ND', menu: 'N', key: 'D',
    symbol: 'Nets_Dfm', label: 'DFM Check…',
  },
  // Grab a whole polyline. The original has Grab Line, which takes the single
  // straight record under the cursor — right for the format, wrong for what a
  // bent track looks like to the person editing it. `W` was free in Grab.
  {
    code: 'GW', menu: 'G', key: 'W',
    symbol: 'Grab_Multiline', label: 'Multiline',
  },
];

/**
 * Commands SirCAD gives a different code from the original's.
 *
 * Every command is two keypresses. Never three, never a modifier — that is the
 * point of the two-letter codes, and it is what makes the program fast to drive
 * once the letters are in the fingers.
 *
 * The original tells commands apart by the *case* of the second letter: `Bt` is
 * cut and `BT` is Track Isolation. Where both halves of such a pair are
 * implemented that is a trap, so the rule is:
 *
 *   · the half that is used more keeps the letters, and typing the other case
 *     reaches it as well — `pickCommand` lands on whichever survivor is
 *     implemented;
 *   · the other moves to a free code in the same menu, another letter or a
 *     digit.
 *
 * Which half that is was first written down as a rule — lowercase keeps the
 * letters — on the reasoning that a capitalised second letter marks one of the
 * original's less frequent commands. That is a fair rule of thumb about *its*
 * tree and it is not a fact about ours, as Edit showed the moment it was
 * applied: there the uppercase half is the item editor, which is reached
 * constantly, and the lowercase half is text-file utilities and one rare
 * geometry command. Following the letter of the rule moved Edit Arc, Edit Pad
 * and Edit Component out of the way of Align, Compare and Find, which is
 * exactly backwards.
 *
 * So the deciding question is which command is reached for more, and the case
 * is only a hint at the answer.
 *
 * Nothing is moved before it has to be. There are thirty-six such pairs in the
 * tree and, at the time of writing, not one has both halves implemented — so
 * this map is empty and typing either case reaches the single command that
 * exists. It fills up when the lowercase halves are built: the text-file
 * commands `Ba`, `By`, `Bt` and `Be` will displace Tear Drop, Copy Clipboard,
 * Track Isolation and Release.
 *
 * Keyed by the code SirCAD uses; the value is the original's, which is what the
 * extracted help is keyed by, so a moved command keeps its own help.
 */
export const RECODED: Record<string, string> = {
  // Edit filled first, and all six pairs at once: Align, Compare, Find, Find
  // Next, Replace Confirm and Line to Arc each ran into an uppercase already
  // built. The item editor keeps its letters - Edit Arc, Pad, Text, Component,
  // Components and Filled Solid are reached constantly, and five of the six
  // that moved are text-file commands that have the editor's own Find bar and
  // Ctrl+F as their real way in.
  //
  // They moved to codes the original never uses anywhere in this menu, so
  // nothing it may yet want is displaced. The one drawing command among them
  // takes the free letter; the text ones take what is left, digits included,
  // which have the merit of having no other case to collide with.
  EW: 'Et', // Line to Arc
  EH: 'Ea', // Align
  EI: 'Ep', // Compare
  EJ: 'Ef', // Find
  E5: 'En', // Find Next
  E8: 'Ec', // Replace Confirm
};

/** The original's code for a command SirCAD has moved. */
export function originalCode(code: string): string {
  return RECODED[code] ?? code;
}

/** Reverse of {@link RECODED}: the code to show for one of the original's. */
const SHOWN_AS = new Map(
  Object.entries(RECODED).map(([shown, original]) => [original, shown]));

/** The code SirCAD shows for one of the original's — the inverse of {@link originalCode}. */
export function displayCode(code: string): string {
  return SHOWN_AS.get(code) ?? code;
}

/**
 * Menus SirCAD adds that the original never had.
 *
 * Same reasoning as EXTRA_COMMANDS: the generated tree cannot hold them. A
 * whole menu is a heavier thing to add than a command, so the bar is higher —
 * this is for work the original had no place for at all, not for commands that
 * would sit happily under an existing letter.
 *
 * The letter has to be one the original left free.
 */
export const EXTRA_MENUS: (Menu & { after?: string })[] = [
  // Beside Edit rather than tacked on the end: looking at the board belongs
  // with working on it, and the tail of the bar is where Help lives.
  { key: 'V', label: 'Views', commands: [], after: 'E' },
];

/** The whole menu tree, the original's and ours, in the order they are shown. */
export const ALL_MENUS: Menu[] = (() => {
  // The generated tree is left exactly as extracted — it is the record of what
  // the original does — and the handful of moved codes are applied on the way
  // past, so re-extracting OG2.h can never quietly undo them.
  const out: Menu[] = MENUS.map((m) => ({
    ...m,
    commands: m.commands.map((c) => {
      const shown = SHOWN_AS.get(c.code);
      return shown ? { ...c, code: shown, key: shown.slice(1) } : c;
    }),
  }));
  for (const m of EXTRA_MENUS) {
    const at = m.after ? out.findIndex((x) => x.key === m.after) : -1;
    if (at < 0) out.push(m);
    else out.splice(at + 1, 0, m);
  }
  return out;
})();

/** The commands of a menu, the original's and ours. */
function commandsOf(menu: Menu): MenuCommand[] {
  const extra = EXTRA_COMMANDS.filter((c) => c.menu === menu.key);
  return extra.length === 0 ? menu.commands : [...menu.commands, ...extra];
}

export interface MenuHost {
  /** Status line feedback, e.g. the pending menu letter. */
  setMessage(msg: string): void;
  /** True when a command should be selectable in the current context. */
  isEnabled?(command: MenuCommand): boolean;
  /**
   * Why a command is unavailable, for when one is typed rather than clicked.
   *
   * A greyed menu item explains itself by being greyed. Typed, there is nothing
   * to see, so the refusal has to say something.
   */
  whyDisabled?(command: MenuCommand): string;
}

/** One row of an open menu: a command to run, or a heading over the next few. */
interface VisibleEntry {
  command?: MenuCommand;
  label: string;
  heading?: boolean;
  /** This heading starts a column of its own. */
  column?: boolean;
}

/**
 * A curated menu entry: a command code, optionally with a display label — or a
 * heading, which names the group of entries under it and does nothing.
 *
 * Headings exist because the File menu is thirty entries long. The original's
 * was a dozen; ours has the imports, the exports and the list of what you were
 * working on that it never had. A column of thirty is not a menu, it is a
 * search, and the recent files at the end of it were being missed entirely.
 *
 * `column` on a heading puts it at the top of a column of its own. Where the
 * menu breaks otherwise depends on how tall the window is, and a list that
 * moves about the menu between one session and the next is a list you have to
 * find each time rather than one you know the place of.
 */
export type MenuEntry =
  | string
  | { code: string; label?: string }
  | { heading: string; column?: boolean };
/** Per-menu curation: exactly these entries, in this order. */
export type MenuLayout = Record<string, MenuEntry[]>;

export class MenuSystem {
  private readonly handlers = new Map<string, CommandHandler>();
  /** Menu letter awaiting its command letter, or null when idle. */
  private pending: string | null = null;
  /** Whether {@link pending} was opened from the keyboard rather than the bar. */
  private pendingByKey = false;
  private bar: HTMLElement | null = null;
  private popup: HTMLElement | null = null;
  /** Explicit contents/order for chosen menus; others show all implemented. */
  private layout: MenuLayout = {};
  /** Menu key → command code run the instant that menu opens (see below). */
  private defaults: Record<string, string> = {};

  private readonly host: MenuHost;

  // Written out rather than declared as a constructor parameter property: that
  // is the one piece of TypeScript here that is not simply erasable, and it
  // stops the module being loaded by the checks in tools/, which want to ask
  // this file what the menu tree looks like.
  constructor(host: MenuHost) {
    this.host = host;
  }

  /**
   * Give a menu a default command that fires the moment the menu opens, while
   * the menu stays open for a refining keystroke. This makes a single letter a
   * usable shortcut — pressing `G` grabs an item straight away, and `G R`, `G X`
   * still reach the rotate/mirror variants — the "grab unless modified" feel.
   */
  setDefaults(map: Record<string, string>): void {
    this.defaults = map;
  }

  /** Curate a menu's contents and order. Codes not implemented are dropped. */
  /**
   * Break a long menu into as many columns as the window has room for.
   *
   * A menu that scrolls hides its own contents — the recent files were at the
   * bottom of one and were being missed entirely — so it grows sideways
   * instead. The number of rows has to be worked out here rather than left to
   * the stylesheet: only the browser knows how tall a row came out and how much
   * room is left under the bar.
   *
   * Measured from the popup as first laid out, in one column. Where there is
   * room for all of it this does nothing at all.
   */
  private fitColumns(popup: HTMLElement): void {
    const rows = popup.childElementCount;
    if (rows === 0) return;

    // How much room there is under the bar, when anything has been laid out at
    // all. A headless check and a hidden page both measure zero.
    const tall = popup.scrollHeight;
    const room = window.innerHeight - popup.getBoundingClientRect().top - 12;
    const measured = tall > 0 && room > 0;
    // The average row, which is near enough: headings are a little taller than
    // entries and there are a handful of them among thirty.
    const each = measured ? tall / rows : 0;

    // A heading asking for a column of its own gets one, whether or not the
    // menu would have fitted in a single column without it. That is the point
    // of asking: the File menu's Re-Open list is what people open the menu for,
    // and it should be in the same place every time rather than wherever this
    // window's height happens to put it.
    const forced = [...popup.children]
      .findIndex((c) => (c as HTMLElement).dataset.column === 'start');
    if (forced > 0 && (!measured || forced * each <= room)) {
      popup.style.setProperty('--menu-rows', String(forced));
      return;
    }

    // Otherwise it only grows sideways when it has to. A menu that scrolls
    // hides its own contents — the recent files were at the bottom of one and
    // were being missed entirely.
    if (!measured || tall <= room) return;
    const perColumn = Math.max(6, Math.floor(room / each));
    popup.style.setProperty('--menu-rows', String(perColumn));
  }

  setLayout(layout: MenuLayout): void {
    this.layout = layout;
    if (this.bar) this.mount(this.bar);
  }

  /**
   * The commands to show for a menu: a curated list when one is set, otherwise
   * every *implemented* command — unimplemented ones are hidden rather than
   * greyed, so a menu only ever offers what actually works.
   */
  private visible(menu: Menu): VisibleEntry[] {
    const custom = this.layout[menu.key];
    if (custom) {
      const out: VisibleEntry[] = [];
      for (const entry of custom) {
        if (typeof entry === 'object' && 'heading' in entry) {
          out.push({ label: entry.heading, heading: true, column: entry.column === true });
          continue;
        }
        const code = typeof entry === 'string' ? entry : entry.code;
        const command = commandsOf(menu).find((c) => c.code === code);
        if (!command || !this.handlers.has(code)) continue;
        const label = (typeof entry === 'object' && entry.label) || command.label;
        out.push({ command, label });
      }
      // A heading with nothing under it is a promise the menu does not keep —
      // which is what the Re-Open heading would be before anything is opened.
      // The next entry has to be a command: `heading` is absent rather than
      // false on those, so testing it against `false` dropped every heading.
      return out.filter((e, i) => {
        if (!e.heading) return true;
        const next = out[i + 1];
        return next !== undefined && next.heading !== true;
      });
    }
    return commandsOf(menu)
      .filter((c) => this.handlers.has(c.code))
      .map((c) => ({ command: c, label: c.label, heading: false }));
  }

  /**
   * Attach a handler to a command code.
   * Unknown codes throw: a typo would otherwise be a command that silently
   * never fires.
   */
  register(code: string, handler: CommandHandler): void {
    if (!findCommand(code)) {
      throw new Error(`No such command code: ${code}`);
    }
    this.handlers.set(code, handler);
  }

  registerAll(map: Record<string, CommandHandler>): void {
    for (const [code, handler] of Object.entries(map)) this.register(code, handler);
  }

  implemented(code: string): boolean {
    return this.handlers.has(code);
  }

  /** Every wired-up command, for the keystroke-macro picker. */
  implementedCommands(): { code: string; label: string }[] {
    const out: { code: string; label: string }[] = [];
    for (const menu of ALL_MENUS) {
      for (const c of commandsOf(menu)) {
        if (this.handlers.has(c.code)) out.push({ code: c.code, label: c.label });
      }
    }
    return out;
  }

  /**
   * Resolve a command letter within the pending menu.
   *
   * Case matters in the original — `Fs` is New Slave and `FS` is Save — so an
   * exact match is tried first. But where one case is implemented and the other
   * is not, the working one wins: `Sw` is Mouse Wheel, which SirCAD does not
   * have, while `SW` is Mirror View, which it does, and typing an unshifted `w`
   * should reach the command that exists rather than report a dead one.
   */
  private pickCommand(menuKey: string, key: string): MenuCommand | undefined {
    const menu = ALL_MENUS.find((m) => m.key === menuKey);
    if (!menu) return undefined;
    const all = commandsOf(menu);
    const exact = all.filter((c) => c.key === key);
    const loose = all.filter(
      (c) => c.key.toUpperCase() === key.toUpperCase());
    return exact.find((c) => this.handlers.has(c.code))
      ?? loose.find((c) => this.handlers.has(c.code))
      ?? exact[0] ?? loose[0];
  }

  /** Run a command by its code, as a keystroke macro does. False if unknown. */
  runByCode(code: string): boolean {
    const command = findCommand(code);
    if (!command || !this.handlers.has(code)) return false;
    this.run(command, { viaKeyboard: true });
    return true;
  }

  // -------------------------------------------------------------------------
  // Keyboard
  // -------------------------------------------------------------------------

  /** Handle a keystroke. Returns true when the menu consumed it. */
  handleKey(ev: KeyboardEvent): boolean {
    if (ev.ctrlKey || ev.metaKey || ev.altKey) return false;

    if (ev.key === 'Escape') {
      if (this.pending === null) return false;
      this.close();
      return true;
    }

    if (ev.key.length !== 1) return false;

    if (this.pending === null) {
      const menu = matchMenu(ev.key);
      if (!menu) return false;
      this.open(menu.key, true);
      return true;
    }

    const command = this.pickCommand(this.pending, ev.key);
    this.close();
    if (!command) {
      this.host.setMessage(`No command ${this.pending}${ev.key}`);
      return true;
    }
    this.run(command, { viaKeyboard: true });
    return true;
  }

  run(command: MenuCommand, how: CommandInvocation = {}): void {
    // Greyed on the menu means unavailable, whichever way it is reached. This
    // was only ever applied when drawing the popup, so typing the two letters
    // ran a command the menu refused to offer - Place Signal Wire on a board,
    // which is a schematic command wired to the schematic's own layer numbers,
    // so it laid its wire on whatever a board keeps at layer two.
    if (this.host.isEnabled?.(command) === false) {
      this.host.setMessage(
        this.host.whyDisabled?.(command)
        ?? `${command.label} (${command.code}) — not available here`);
      return;
    }

    const handler = this.handlers.get(command.code);
    if (!handler) {
      this.host.setMessage(
        `${command.label} (${command.code}) — in the original, not yet in SirCAD`);
      return;
    }
    // Name the command first, so one that reports nothing itself still clears
    // the "press a command letter" prompt rather than leaving it standing.
    this.host.setMessage(`${command.label} (${command.code})`);
    void handler(how);
  }

  // -------------------------------------------------------------------------
  // Bar and popup
  // -------------------------------------------------------------------------

  mount(bar: HTMLElement): void {
    this.bar = bar;
    bar.replaceChildren();

    for (const menu of ALL_MENUS) {
      // A menu with nothing implemented (or curated to nothing) is not shown.
      if (this.visible(menu).length === 0) continue;

      const btn = document.createElement('button');
      btn.className = 'menu-btn';
      btn.dataset.menu = menu.key;
      btn.title = `${menu.label} — press ${menu.key}`;

      // The access letter is shown underlined, as the original does.
      const label = menu.label;
      const i = label.toUpperCase().indexOf(menu.key.toUpperCase());
      if (i >= 0) {
        btn.append(
          document.createTextNode(label.slice(0, i)),
          Object.assign(document.createElement('u'), { textContent: label[i] }),
          document.createTextNode(label.slice(i + 1)),
        );
      } else {
        btn.append(
          document.createTextNode(`${label} `),
          Object.assign(document.createElement('u'), { textContent: menu.key }),
        );
      }

      btn.addEventListener('click', (e) => {
        e.stopPropagation();
        if (this.pending === menu.key) this.close();
        else this.open(menu.key);
      });
      bar.append(btn);
    }

    // A click anywhere else dismisses an open menu.
    document.addEventListener('click', () => { if (this.pending) this.close(); });
  }

  private open(menuKey: string, byKey = false): void {
    this.pending = menuKey;
    this.pendingByKey = byKey;
    const menu = ALL_MENUS.find((m) => m.key === menuKey);
    if (!menu || !this.bar) return;

    this.bar.querySelectorAll('.menu-btn').forEach((b) => {
      b.classList.toggle('open', (b as HTMLElement).dataset.menu === menuKey);
    });

    this.popup?.remove();
    const popup = document.createElement('div');
    popup.className = 'menu-popup';

    for (const { command, label: text, heading, column } of this.visible(menu)) {
      if (heading || !command) {
        const h = document.createElement('div');
        h.className = 'menu-heading';
        h.textContent = text;
        // Left for fitColumns to find, rather than counted here: a heading with
        // nothing under it has already been dropped by then, so counting now
        // would break the column at a row that is not on the menu.
        if (column) h.dataset.column = 'start';
        popup.append(h);
        continue;
      }
      const item = document.createElement('button');
      const enabled = this.host.isEnabled?.(command) ?? true;
      item.className = 'menu-item' + (enabled ? '' : ' disabled');
      item.disabled = !enabled;

      const key = document.createElement('span');
      key.className = 'mi-key';
      key.textContent = command.key;

      const label = document.createElement('span');
      label.className = 'mi-label';
      label.textContent = text;

      item.append(key, label);
      // Hovering says what the command is for, in a line, from the original's
      // own context help. The code stays on the end for anyone who drives by
      // keystroke and wants to learn the two letters.
      const help = tipFor(command.code);
      item.title = help
        ? `${help}\n\n${command.code}`
        : `${command.code} · ${command.symbol}`;
      item.addEventListener('click', (e) => {
        e.stopPropagation();
        this.close();
        this.run(command);
      });
      popup.append(item);
    }

    const anchor = this.bar.querySelector<HTMLElement>(`[data-menu="${cssEscape(menuKey)}"]`);
    const rect = anchor?.getBoundingClientRect();
    if (rect) {
      popup.style.left = `${Math.round(rect.left)}px`;
      popup.style.top = `${Math.round(rect.bottom)}px`;
    }

    document.body.append(popup);
    this.popup = popup;
    this.fitColumns(popup);
    this.host.setMessage(`${menu.label}: press a command letter, Esc to cancel`);

    // A menu with a default runs it now and stays open, so the letter alone acts
    // (G = grab item) while a follow-up letter still selects a variant.
    const def = this.defaults[menuKey];
    const defCmd = def ? findCommand(def) : undefined;
    if (def && defCmd && this.handlers.has(def)) {
      void this.handlers.get(def)!({ fromDefault: true, viaKeyboard: this.pendingByKey });
      this.host.setMessage(
        `${menu.label}: ${defCmd.label} — press a key to modify, Esc to finish`);
    }
  }

  private close(): void {
    this.pending = null;
    this.popup?.remove();
    this.popup = null;
    this.bar?.querySelectorAll('.menu-btn').forEach((b) => b.classList.remove('open'));
  }

  get isOpen(): boolean {
    return this.pending !== null;
  }
}

// ---------------------------------------------------------------------------

function matchMenu(key: string): Menu | undefined {
  return ALL_MENUS.find((m) => m.key === key)
    ?? ALL_MENUS.find((m) => m.key.toUpperCase() === key.toUpperCase());
}

export function findCommand(code: string): MenuCommand | undefined {
  for (const menu of ALL_MENUS) {
    const hit = commandsOf(menu).find((c) => c.code === code);
    if (hit) return hit;
  }
  return undefined;
}

/** Minimal CSS.escape, which older Safari lacks. */
function cssEscape(s: string): string {
  return s.replace(/["\\]/g, '\\$&');
}
